import { templateGridSize } from "../design/designer";
import type { DesignItemRef } from "../design/design.types";
import type { ProductRef } from "../products/product";
import type { LayoutItem, LayoutItemRef } from "./layouts";

/**
 * How a layout assigns products to its elements, surfaced in the Layout Designer
 * so users can see exactly how many distinct products a page will consume.
 *
 * Mirrors the catalog render (cm1 `InjectLayoutItems` / `injectLayoutItems`):
 * each "product element" pulls one product from the page's pool in render order.
 * Elements that share the same Product Key (`di.title`) REUSE the same product
 * instead of each consuming their own. A field-header built as a Product Text
 * with no shared key therefore silently eats an extra product per page — this is
 * what powers the Tier-1 summary + Tier-2 per-element badges.
 *
 * `consumesProduct` and `layoutSlotSort` are the SINGLE source of truth for both
 * this badge engine AND `injectLayoutItems` (the real render path). Keeping one
 * predicate + one sort guarantees the badges / preview / product names agree with
 * what actually renders — they used to drift (badge under-counted image fields and
 * numbered slots in a different order than products filled), which is a root cause
 * of the "why did my layout eat a product?" confusion.
 */
export type LayoutProductSlots = {
  /** product slot number (1-based) per item id; absent for non-product elements */
  slotById: Map<string, number>;
  /** product key (di.title) per item id, when one is set */
  keyById: Map<string, string>;
  /** which slot numbers are occupied by a product card */
  cardSlots: Set<number>;
  /** distinct products this layout places on each page (lists not counted) */
  total: number;
  /** how many of those slots are product cards */
  cardCount: number;
  /** a product/category list element is present (adds a variable extra count) */
  hasList: boolean;
  /** a non-card element consumes its own product — the silent-drop smell */
  warn: boolean;
};

const isListItem = (i: LayoutItemRef) => !!i.info?.extension;

/**
 * Does this layout element pull a product from the page pool? This is the EXACT
 * boolean `injectLayoutItems` uses to decide whether to `products.shift()`, so the
 * two never disagree. It intentionally does NOT special-case Extension elements —
 * the render path never did either; the badge engine excludes lists in its own
 * pre-filter (`isListItem`) so a bare product/category list isn't counted as a
 * fixed slot, which is the behaviour that already matched the render in practice.
 */
export const consumesProduct = (li: LayoutItem): boolean =>
  (((li.title || li.subtype === "product") && li.type === "text") ||
    ((li.field || li.text) && li.type === "image") ||
    li.type === "template") === true;

/**
 * Render order in which product elements fill their slots — quantizes y to the
 * nearest 5 (so a visual row reads left-to-right) then sorts by x. Identical to
 * the comparator `injectLayoutItems` has always used, extracted so slot numbering
 * matches the order products are actually bound.
 */
export const layoutSlotSort = (a: LayoutItem, b: LayoutItem): number =>
  Math.round((a.y - b.y) / 5) * 5 * templateGridSize * 10 + a.x - b.x;

export const getLayoutProductSlots = (
  pageItems: DesignItemRef[]
): LayoutProductSlots => {
  const items = pageItems as LayoutItemRef[];

  const slotItems = items
    .filter((i) => i.type !== "page" && !isListItem(i) && consumesProduct(i.di))
    .slice()
    .sort((a, b) => layoutSlotSort(a.di, b.di));

  const keyToSlot = new Map<string, number>();
  const slotById = new Map<string, number>();
  const keyById = new Map<string, string>();
  const cardSlots = new Set<number>();
  let total = 0;

  for (const it of slotItems) {
    const key = (it.di.title ?? "").trim();
    let slot: number;
    if (key && keyToSlot.has(key)) {
      slot = keyToSlot.get(key)!; // shared Product Key -> reuse one product
    } else {
      total += 1;
      slot = total;
      if (key) keyToSlot.set(key, slot);
    }
    slotById.set(it.id, slot);
    if (key) keyById.set(it.id, key);
    if (it.type === "template") cardSlots.add(slot);
  }

  const cardCount = cardSlots.size;

  return {
    slotById,
    keyById,
    cardSlots,
    total,
    cardCount,
    hasList: items.some(isListItem),
    warn: cardCount > 0 && total > cardCount,
  };
};

/**
 * Bind real products to slot numbers for the in-editor preview + layers labels.
 * Slot numbers are assigned in `layoutSlotSort` order and `injectLayoutItems`
 * shifts products in that same order, so slot k always maps to `products[k - 1]`.
 * Fewer products than slots -> the tail slots stay unbound (placeholder).
 */
export const bindSlotProducts = (
  slots: LayoutProductSlots,
  products: ProductRef[]
): Map<number, ProductRef> => {
  const productBySlot = new Map<number, ProductRef>();
  for (let slot = 1; slot <= slots.total; slot++) {
    const p = products[slot - 1];
    if (p) productBySlot.set(slot, p);
  }
  return productBySlot;
};
